/* External */
import { useIntl } from 'react-intl';
import { isWithinInterval, parseISO } from 'date-fns';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Theme Assets */
import { FORGEROCK_ACCOUNT_TYPES, USER_TYPES } from '@dtvgo/utils';
import iconStreaming from '~/assets/theme/svg/streaming.svg';
import iconPlayerLocked from '~/assets/theme/svg/player-locked.svg';

/* Ott-common */

/* Hooks */
import { useUserAccount } from '../../providers/UserProvider';

/* Utils */
import {
  BUSINESS_CAMPAIGN,
  Country,
  OTT_SSLA_DOMAINS,
  DTH_OTT_SSLA_DOMAINS,
  MessagesUpSellBrazilUser,
  MessagesUpSellSSLAUser,
  MessagesUpSellGranHermanoUser,
  MessagesUpSellUserB2B2C,
} from '../../utils/constants';
import {
  IS_UPSELL_GRAN_HERMANO_ACTIVE,
  UPSELL_URL_OTT_DTH_TVE_SSLA,
  IS_UPSELL_PAY_PER_VIEW_ACTIVE,
  UPSELL_PAY_PER_VIEW_COUNTRIES,
  UPSELL_PAY_PER_VIEW_CHANNELS,
  UPSELL_PAY_PER_VIEW_URL,
  UPSELL_PAY_PER_VIEW_START_DATE,
  UPSELL_PAY_PER_VIEW_END_DATE,
  UPSELL_PAY_PER_VIEW_TITLE,
  UPSELL_PAY_PER_VIEW_DESCRIPTION,
  UPSELL_PAY_PER_VIEW_LOGO_URL,
  UPSELL_PAY_PER_VIEW_BACKGROUND_URL,
  isUpSellMsoMessageActive,
} from '../../utils/env';

const sourceErrorMessages = {
  title: 'player.error.source.title',
  subtitle: 'player.error.description',
};

const messageDefault = {
  subtitle: 'player.error.entitlements.title',
};

function formatTextLineWrapping(text) {
  return text.replace(/\\n/g, '\n');
}

export function getUpSellPayPerViewData() {
  return {
    startDate: UPSELL_PAY_PER_VIEW_START_DATE,
    endDate: UPSELL_PAY_PER_VIEW_END_DATE,
    countryList: UPSELL_PAY_PER_VIEW_COUNTRIES,
    channelList: UPSELL_PAY_PER_VIEW_CHANNELS,
    flows: {
      register: {
        highlightImageURL: '',
        backgroundImageURL: '',
        title: '',
        subtitle: '',
        linkURL: '',
        primaryButtonTitle: '',
        secondaryButtonTitle: '',
        welcomeRegisterButtonTitle: '',
      },
      buyflow: {
        highlightImageURL: UPSELL_PAY_PER_VIEW_LOGO_URL,
        backgroundImageURL: UPSELL_PAY_PER_VIEW_BACKGROUND_URL,
        title: formatTextLineWrapping(UPSELL_PAY_PER_VIEW_TITLE),
        subtitle: formatTextLineWrapping(UPSELL_PAY_PER_VIEW_DESCRIPTION),
        linkURL: UPSELL_PAY_PER_VIEW_URL,
        primaryButtonTitle: 'Ya compré',
        secondaryButtonTitle: 'Ahora no',
        qrCodeTitle: '¡MIRÁ EL PARTIDO EN EXCLUSIVA!',
      },
    },
  };
}

/* eslint sonarjs/cognitive-complexity: "off" -- Mark as warning to fix in the future */
export function useErrorMessages(error, contentTitle, channelId) {
  const { formatMessage } = useIntl();
  const iconPlayerLockedFlag = getJsonImage('player-locked') || iconPlayerLocked;
  const iconStreamingFlag = getJsonImage('streaming') || iconStreaming;

  const {
    iso2Code,
    msoProvider,
    businessUnit,
    accountType: userType,
    selfcareUrl,
  } = useUserAccount() || {};
  const isGranHermanoUser = BUSINESS_CAMPAIGN.has(msoProvider);
  const isBrazilUser = iso2Code.toLowerCase() === Country.BRAZIL;
  const isArgentinaUser = iso2Code.toLowerCase() === Country.ARGENTINA;
  const isMexicoUser = iso2Code.toLowerCase() === Country.MEXICO;
  let upSellMessage = messageDefault;

  const UpSellPayPerViewData = getUpSellPayPerViewData();
  const {
    countryList, channelList, startDate, endDate,
  } = UpSellPayPerViewData;
  const rightNowDate = new Date();
  const isBetweenTheStartDateAndEndDate = isWithinInterval(
    rightNowDate,
    {
      start: parseISO(startDate),
      end: parseISO(endDate),
    },
  );

  const isAPayPerViewUpSell = IS_UPSELL_PAY_PER_VIEW_ACTIVE
    && countryList.has(iso2Code.toLowerCase()) && channelList.has(channelId)
    && isBetweenTheStartDateAndEndDate;

  const isAPayPerViewUpSellGranHermano = isAPayPerViewUpSell && isGranHermanoUser;

  if (isAPayPerViewUpSellGranHermano) {
    const { flows: { buyflow } } = UpSellPayPerViewData;
    const {
      title,
      subtitle,
      linkURL,
      qrCodeTitle,
      backgroundImageURL,
      highlightImageURL,
      primaryButtonTitle,
      secondaryButtonTitle,
    } = buyflow;

    return {
      isAPayPerViewUpSell,
      isAPayPerViewUpSellGranHermano,
      title,
      subtitle,
      linkURL,
      qrCodeTitle,
      backgroundImageURL,
      highlightImageURL,
      primaryButtonTitle,
      secondaryButtonTitle,
    };
  }

  const mustSetGranHermanoMessages = IS_UPSELL_GRAN_HERMANO_ACTIVE
    && (isGranHermanoUser || isArgentinaUser);

  if (isBrazilUser) {
    upSellMessage = MessagesUpSellBrazilUser[businessUnit];
  } else if (mustSetGranHermanoMessages) {
    if (!isGranHermanoUser && isArgentinaUser) {
      upSellMessage = MessagesUpSellGranHermanoUser.userOTT_DTH_TVE_Argentina;
      upSellMessage = {
        ...upSellMessage,
        link: `${upSellMessage.link + DTH_OTT_SSLA_DOMAINS[iso2Code] + UPSELL_URL_OTT_DTH_TVE_SSLA}`,
        qrLink: `${upSellMessage.link + DTH_OTT_SSLA_DOMAINS[iso2Code] + UPSELL_URL_OTT_DTH_TVE_SSLA}`,
      };
    } else if (isGranHermanoUser && isArgentinaUser) {
      upSellMessage = MessagesUpSellGranHermanoUser.userGranHermanoArgentina;
      upSellMessage = {
        ...upSellMessage,
        link: `${upSellMessage.link + OTT_SSLA_DOMAINS.gh}`,
        qrLink: `${upSellMessage.link + OTT_SSLA_DOMAINS.gh}`,
      };
    } else {
      upSellMessage = MessagesUpSellGranHermanoUser.userGranHermanoSSLA;
      upSellMessage = {
        ...upSellMessage,
        link: `${upSellMessage.link + OTT_SSLA_DOMAINS.ya}`,
        qrLink: `${upSellMessage.link + OTT_SSLA_DOMAINS.ya}`,
      };
    }
  } else {
    if (businessUnit === FORGEROCK_ACCOUNT_TYPES.SSLA_OTT) {
      upSellMessage = MessagesUpSellSSLAUser[businessUnit];

      let upSellLink = upSellMessage.link + OTT_SSLA_DOMAINS.ya;

      if (isArgentinaUser) {
        upSellLink = MessagesUpSellGranHermanoUser.userOTT_DTH_TVE_Argentina.link
          + DTH_OTT_SSLA_DOMAINS[iso2Code] + UPSELL_URL_OTT_DTH_TVE_SSLA;
      }

      upSellMessage = {
        ...upSellMessage,
        link: upSellLink,
      };
    }

    if (!isMexicoUser
      && (businessUnit === FORGEROCK_ACCOUNT_TYPES.SSLA_DTH
        || businessUnit === FORGEROCK_ACCOUNT_TYPES.LATAM_OTT
        || businessUnit === FORGEROCK_ACCOUNT_TYPES.LATAM_DTH)) {
      upSellMessage = MessagesUpSellSSLAUser[FORGEROCK_ACCOUNT_TYPES.SSLA_DTH];
      upSellMessage = {
        ...upSellMessage,
        link: `${upSellMessage.link + DTH_OTT_SSLA_DOMAINS[iso2Code] + UPSELL_URL_OTT_DTH_TVE_SSLA}`,
      };
    }

    if (businessUnit === FORGEROCK_ACCOUNT_TYPES.MSO_DTH) {
      upSellMessage = MessagesUpSellSSLAUser[businessUnit];
    }
  }

  if (!isAPayPerViewUpSellGranHermano
    && userType === USER_TYPES.OTT_B2B2C
    && isUpSellMsoMessageActive) {
    upSellMessage = {
      ...(selfcareUrl
        ? MessagesUpSellUserB2B2C.userB2B2C
        : MessagesUpSellUserB2B2C.userB2B2CWithoutSelfcareLink),
      qrLink: selfcareUrl || undefined,
      highlightImageURL: selfcareUrl ? undefined : iconPlayerLocked,
    };
  }

  let translations = upSellMessage;

  if (!translations && error.isSourceError) translations = sourceErrorMessages;

  const values = { contentTitle };

  if (translations) {
    const hasQrLinkProperty = 'qrLink' in translations;
    return {
      isAPayPerViewUpSell,
      isAPayPerViewUpSellGranHermano,
      title: translations.title
        && formatMessage({ id: translations.title }, values),
      subtitle: translations.subtitle
        && formatMessage({ id: translations.subtitle }, values),
      primaryButtonTitle: translations.button
        && formatMessage({ id: translations.button }, values),
      linkURL: translations?.link,
      ...(hasQrLinkProperty && { qrLink: translations?.qrLink }),
      highlightImageURL: translations?.highlightImageURL
        ?? (businessUnit === FORGEROCK_ACCOUNT_TYPES.MSO_DTH
          ? iconPlayerLockedFlag : iconStreamingFlag),
    };
  }

  return {
    title: '',
    subtitle: formatMessage({ id: 'player.error.title' }, values),
  };
}
